import {
  Brand as BrandUi,
  Button,
  Card,
  CardContent,
  Chip,
  Division,
  Flex,
  Icon,
  Icons,
  Paragraph,
} from "@ir/ui";
import { useNavigate } from "react-router";
import type { Brand, BrandFeature } from "@src/domain/entities/brand";
import { isActiveBrandFeature } from "./brandFeatureUtils";
import { BrandStatusControl } from "./BrandStatusControl";

export interface BrandWithFeatures extends Brand {
  brandFeatures: BrandFeature[] | null;
}

export interface BrandCardProps {
  brand: BrandWithFeatures;
  organizationKey?: string;
  organizationId?: number;
  onUpdateBrandStatus?: (brandId: number, brandIsActive: boolean) => Promise<void>;
  onUpdateSuccess?: () => void;
}

export const BrandCard = ({
  brand,
  organizationKey,
  organizationId,
  onUpdateBrandStatus,
  onUpdateSuccess,
}: BrandCardProps) => {
  const navigate = useNavigate();
  const displayName = brand.brandDisplayName ?? brand.brandName;
  const features = (brand.brandFeatures ?? []).filter(isActiveBrandFeature);
  const href =
    organizationKey == null
      ? undefined
      : `/organizations/${organizationKey}/${brand.brandKey}`;

  return (
    <Card className="bg-white mb-4" variant="outlined" shadow>
      <CardContent>
        <Flex align="center" className="mb-3">
          <BrandUi
            size="small"
            brandName={displayName}
            logoSrcCompact={brand.brandLogoUrl ?? undefined}
          />
          <Division style={{ marginLeft: "1rem", flex: 1 }}>
            {href ? (
              <Button
                size="small"
                noStyle
                htmlTitle="View Brand Details"
                onPress={() =>
                  navigate(href, {
                    state:
                      organizationId == null ? undefined : { organizationId },
                  })
                }
              >
                <Paragraph
                  className="text-grey-700 font-bold m-0"
                  style={{ lineHeight: "1" }}
                >
                  {displayName}
                </Paragraph>
              </Button>
            ) : (
              <Paragraph
                className="text-grey-700 font-bold m-0"
                style={{ lineHeight: "1" }}
              >
                {displayName}
              </Paragraph>
            )}
            <Flex
              className="m-0 p-0 text-grey-500 font-regular text-sm"
              style={{ fontSize: "0.65rem" }}
              align="center"
              gap={0.25}
            >
              <Flex style={{ paddingBottom: "0.1rem" }}>Key: </Flex>
              <Flex className="font-bold me-2">{brand.brandKey}</Flex>
              <Flex>
                <Chip
                  style={{
                    fontSize: "0.65rem",
                    paddingBottom: "0.2rem",
                  }}
                  htmlTitle="Copy Key"
                  size="medium"
                  color="grey"
                  borderless
                  shape="square"
                  icon={
                    <Icon
                      size="small"
                      name={Icons.Copy}
                      style={{ maxHeight: "14px" }}
                    />
                  }
                  onClick={() => {
                    navigator.clipboard.writeText(brand.brandKey);
                  }}
                />
              </Flex>
            </Flex>
          </Division>
          <BrandStatusControl
            brandId={brand.brandId}
            brandIsActive={brand.brandIsActive}
            brandDeletedAt={brand.brandDeletedAt}
            onUpdateBrandStatus={onUpdateBrandStatus}
            onUpdateSuccess={onUpdateSuccess}
          />
        </Flex>
        <Flex align="center" gap={0.5} wrap="wrap">
          {features.length > 0 &&
            features.map((f) => (
              <Chip
                key={f.brandFeatureId}
                size="small"
                borderless
                label={f.brandFeatureName}
              />
            ))}
        </Flex>
      </CardContent>
    </Card>
  );
};
